import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { Box, Typography, Stack, SvgIcon, Link, Tooltip } from "@mui/material";
import { ReactComponent as IconCheck } from "../../../assets/IconCheck.svg";
import { ReactComponent as ArrowIcon } from "../../../assets/ArrowRightWhiteIcon.svg";

import TargetCompletedAnimation from "../../../assets/TargetCompletedAnimation.json";
import TargetScoreLessThan80Animation from "../../../assets/TargetScoreLessThan80Animation.json";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { Module, Question, Target, Course } from "../../utils/CourseTree";
import { WrapperCenterMessage } from "../../v2/learning/WrapperCenterMessage";
import {
  CourseMetadata,
  QuestionMetadata,
  TargetMetadata,
  UserQuizSubmission,
} from "@backend-types";
import { ButtonZoom } from "../../v2/utils/ButtonZoom";
import { Role, _AuthType } from "../../../enums";
import { Class, ComputedQuizScore, DbUser, UserCtx } from "../../../interfaces";
import { StatusCard } from "./StatusCard";
import { RateDigit } from "./RateDigit";
import { ModuleProgressIndicator } from "../../v2/utils/ModuleProgressIndicator";
import { ToastTutorial } from "./ToastTutorial";
import useStorageState from "../../../hooks/useStorageState";
import { generateGtmEvent } from "../../../Helpers";
import { UserContext } from "../../../context/UserContext";

const useStyles = makeStyles((theme) => ({
  root: {
    width: 427,
    position: "relative",
  },
  rootv2: {
    marginTop: "96px",
    marginBottom: "96px",
    width: 552,
    position: "relative",
  },
  button: {
    padding: "17px 32px",
  },
  statistic: {
    color: "#747474",
  },
  statisticVal: {
    color: "#747474",
    fontWeight: theme.typography.fontWeightBold,
  },
  animationContainer: {
    width: "100%",
    position: "absolute",
    top: -251,
    left: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  targetCompletedAnimation: {
    width: 284,
    height: 284,
  },
  targetCompletedAnimationSmall: {
    width: 284,
    height: 284,
  },
  arrow: {
    marginLeft: "8px",
    width: "17px",
    height: "19px",
  },
  link: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    "&:focus": {
      textDecoration: "underline",
    },
  },
  reviewButton: {
    display: "inline-block",
    padding: "16px",
    borderRadius: "8px",
    background: "transparent",
    border: "none",
    gap: "8px",
    "&:hover": {
      cursor: "pointer",
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:hover span": {
      textDecoration: "underline",
    },
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:active": {
      background: "rgba(0, 0, 0, 0.08)",
    },
  },
  percentageStatus: {
    marginLeft: "auto",
    fontWeight: 600,
    fontSize: "1.25em",
    color: theme.palette.text.primary,
    letterSpacing: "0.15px",
    fontFamily: theme.typography.fontFamily,
  },
  percentageStatusV2: {
    marginLeft: "24px",
    fontWeight: 600,
    fontSize: "1.25em",
    color: theme.palette.neutral.dark,
    letterSpacing: "0.15px",
    fontFamily: theme.typography.fontFamily,
  },
  barButton: {
    height: "52px",
    borderRadius: "12px",
    padding: "0px 16px",
  },
  barButtonBasic: {
    color: "#747474",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "20px",
    background: "transparent",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:active": {
      background: "rgba(0, 0, 0, 0.08)",
    },
  },
}));

interface Lti1p3EndTargetProps {
  currentModule: Module;
  currentTarget: Target;
  currentClass?: Class;
  currentCourse: Course | null;
  currentQuestion: Question;
  nextQuestion: Question | null | undefined;
  nextTarget: Target | null | undefined;
  nextModule: Module | null | undefined;
  currentUser: DbUser;
  moduleQuizSubmissions: UserQuizSubmission[];
  targetQuizSubmissions: UserQuizSubmission[];
  targetMetadata?: TargetMetadata;
  courseMetadata?: CourseMetadata;
  handleMoveForward: () => void;
  handleReview: () => void;
  handleModuleHome: () => void;
  handleBackToCanvas: () => void;
  handleDismissAdditionalLearning: () => void;
}

export const Lti1p3EndTarget = (props: Lti1p3EndTargetProps) => {
  const {
    currentTarget,
    currentModule,
    currentClass,
    currentCourse,
    targetMetadata,
    courseMetadata,
    nextQuestion,
    currentUser,
    handleMoveForward,
    handleReview,
    handleModuleHome,
    handleBackToCanvas,
    handleDismissAdditionalLearning,
  } = props;
  const userCtx = useContext(UserContext) as UserCtx;
  const [moduleLastSeen, setModuleLastSeen] = useStorageState({
    storageKey: "tutorialToast-lastModule",
  });
  const [tutorialToastOpen, setTutorialToastOpen] = useState(false);

  const hasSupplementalLearning =
    !targetMetadata?.disable_learning_map_supplemental_quiz_data;
  const languageOverrides = courseMetadata?.language_overrides;
  const useV2TargetEnd = courseMetadata?.learning_experience?.use_v2_target_end;
  const targetLangOverrides = languageOverrides?.target;
  const classes = useStyles();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const currentModuleComplete = currentModule?.isCompleted();
  const currentModuleName = currentModule.getFormattedModuleName();
  const currentTargetName = currentTarget.getFormattedTargetName();
  const useAWSTargetEnd = courseMetadata?.learning_experience?.use_v2_aws_target_end;
  const showRateDigit =
    useAWSTargetEnd ||
    (courseMetadata?.deeplink_asset_type !== "module" &&
      courseMetadata?.deeplink_asset_type !== "course");

  const authMode = userCtx.authHook.authType;
  const closeTabMessage =
    authMode === _AuthType.LTI &&
    currentUser.role === Role.student &&
    courseMetadata?.deeplink_asset_type !== "module" &&
    courseMetadata?.deeplink_asset_type !== "course" &&
    !nextQuestion;

  // const getEndOfTargetMessage = () => {
  //   return (
  //     <Typography sx={{ marginTop: 8 }}>
  //       <strong> Congratulations! </strong>
  //       You have reached the end of this assignment.
  //       <Typography variant={"body1"} sx={{ marginTop: 4 }}>
  //         Please close the browser tab to launch your next assignment from your course in
  //         canvas.
  //       </Typography>
  //     </Typography>
  //   );
  // };

  const quizScore = useMemo<ComputedQuizScore>(() => {
    return userCtx.calculateQuizScore([currentTarget]);
  }, [currentTarget, userCtx]);

  useEffect(() => {
    if (!currentModule || !quizScore) return;
    if (
      currentModule.id !== moduleLastSeen &&
      quizScore.percentage !== null &&
      quizScore.percentage < 0.8 &&
      hasSupplementalLearning
    ) {
      setModuleLastSeen(currentModule.id);
      if (!currentUser.metadata?.le_dismissed_additional_learning) {
        setTutorialToastOpen(true);
      }
    }
  }, [
    setModuleLastSeen,
    currentModule.id,
    currentModule,
    moduleLastSeen,
    quizScore,
    hasSupplementalLearning,
    currentUser.metadata?.le_dismissed_additional_learning,
  ]);

  useEffect(() => {
    if (lottieRef?.current) {
      lottieRef?.current.play();
    }
  }, []);

  let reviewButton = {
    iconClasses: "fa-regular fa-file-pen",
    buttonText: "Review",
  };

  if (quizScore.percentage !== null && quizScore.percentage < 0.8) {
    reviewButton = {
      iconClasses: "fa-regular fa-redo",
      buttonText: "Try again",
    };
  }

  if (quizScore.invalid === "No Quiz") {
    reviewButton = {
      iconClasses: "fa-solid fa-compass",
      buttonText: "Explore again",
    };
  }

  const metadataTotals = useMemo<QuestionMetadata>(() => {
    return currentTarget.getQuestionMetadata();
  }, [currentTarget]);

  const averagePercent = quizScore.percentage !== null ? quizScore.percentage : 0;

  return !useV2TargetEnd ? (
    <WrapperCenterMessage>
      <Box className={classes.root}>
        <Box sx={{ padding: "0px 21px" }}>
          {/* Empty Stack added to ensure proper screen reader hierarchy and prevent heading detection issues */}
          <Stack direction="row" alignItems="center"></Stack>
          <Typography
            variant="h1"
            sx={{
              display: "block",
              lineHeight: "24px",
              fontWeight: 700,
              fontSize: 24,
            }}>
            <Stack direction="row" alignItems="center" sx={{ marginBottom: "16px" }}>
              <IconCheck
                style={{
                  width: "16px",
                  height: "16px",
                  color: "#48A84A",
                  marginRight: "8px",
                }}
                aria-hidden="true"
              />
              <Typography
                variant="body2"
                lineHeight="16px"
                fontWeight={400}
                fontSize={16}
                sx={{ letterSpacing: "1.6px" }}
                textTransform="uppercase">
                {targetLangOverrides?.singular ?? "Learning Target"} Completed
              </Typography>
            </Stack>
            {currentTargetName}
          </Typography>
          {/* Percentage Display */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" className={classes.statistic}>
              {courseMetadata?.language_overrides?.module?.singular ?? "Module"} progress
            </Typography>
            <Typography variant="body2" className={classes.statisticVal}>
              {Math.round(Number(currentModule.progress ?? 0))}%
            </Typography>
          </Stack>
          {/* {courseMetadata?.use_bb_gradarius_endpoint && getEndOfTargetMessage()} */}
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: "48px" }}>
          {closeTabMessage ? (
            <Typography variant="body1" fontSize="1.1250em" color="#484848">
              {courseMetadata?.language_overrides?.lti_assignment_end_message ??
                "Please close the browser tab to launch your next assignment from your course in Canvas."}
            </Typography>
          ) : (
            <>
              <ButtonZoom
                className={classes.button}
                size="medium"
                variant="outlined"
                color="primary"
                onClick={() => {
                  const buttonText = `${
                    courseMetadata?.language_overrides?.module?.singular ?? "Module"
                  } home`.toLowerCase();
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "footer",
                    section: "primary footer",
                    text: buttonText,
                  });
                  handleModuleHome();
                }}>
                {courseMetadata?.language_overrides?.module?.singular ?? "Module"} home
              </ButtonZoom>
              <ButtonZoom
                className={classes.button}
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => {
                  const buttonText = `${
                    currentModuleComplete
                      ? "Move forward"
                      : nextQuestion
                      ? `Next ${
                          targetLangOverrides?.short_singular?.toLowerCase() ??
                          targetLangOverrides?.singular?.toLowerCase() ??
                          "target"
                        }`
                      : "Exit"
                  }`.toLowerCase();
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "footer",
                    section: "primary footer",
                    text: buttonText,
                  });
                  handleMoveForward();
                }}>
                {currentModuleComplete
                  ? "Move forward"
                  : nextQuestion
                  ? `Next ${
                      targetLangOverrides?.short_singular?.toLowerCase() ??
                      targetLangOverrides?.singular?.toLowerCase() ??
                      "target"
                    }`
                  : "Exit"}
                <SvgIcon className={classes.arrow}>
                  <ArrowIcon />
                </SvgIcon>
              </ButtonZoom>
            </>
          )}
        </Stack>
        <Box className={classes.animationContainer}>
          {averagePercent >= 0.8 ? (
            <Lottie
              lottieRef={lottieRef}
              className={classes.targetCompletedAnimation}
              animationData={TargetCompletedAnimation}
              loop={false}
              autoplay={false}
            />
          ) : quizScore.invalid !== "No Quiz" ? (
            <Lottie
              lottieRef={lottieRef}
              className={classes.targetCompletedAnimation}
              animationData={TargetScoreLessThan80Animation}
              loop={false}
              autoplay={false}
            />
          ) : null}
        </Box>
      </Box>
    </WrapperCenterMessage>
  ) : (
    <WrapperCenterMessage
      buttons={
        <>
          <Box
            sx={{
              position: "fixed",
              right: { xs: "28px", lg: "86px" },
              bottom: "97px",
            }}>
            <ToastTutorial
              open={tutorialToastOpen}
              onClose={() => {
                handleDismissAdditionalLearning();
                setTutorialToastOpen(false);
              }}
              popoverId="additional-learning-container">
              <Box sx={{ width: "100%", marginBottom: "24px" }}>
                <video
                  autoPlay
                  muted
                  playsInline
                  loop
                  tabIndex={0}
                  aria-labelledby="video-description"
                  style={{
                    width: "100%",
                    height: "auto",
                    overflow: "hidden",
                  }}>
                  <source
                    src="https://macs-assets.s3.us-east-1.amazonaws.com/completion-learningmap.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </Box>
              <Typography
                variant="h3"
                fontSize="20px"
                lineHeight="28px"
                fontWeight={700}
                color="#FFFFFF"
                sx={{ marginBottom: "26px" }}>
                Additional learning
              </Typography>
              <Typography
                id="video-description"
                variant="body1"
                fontSize="16px"
                lineHeight="24px"
                fontWeight={400}
                color="#FFFFFF">
                Use the plus sign next to each lesson to find additional videos and
                lessons, or the toggle button at the bottom of the page to view all
                learning activities.
              </Typography>
            </ToastTutorial>
          </Box>
          <ButtonZoom
            className={classes.barButton}
            size="large"
            variant="outlined"
            color="primary"
            onClick={() => {
              const buttonText = `${
                courseMetadata?.language_overrides?.module?.singular ?? "Module"
              } home`.toLowerCase();
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "footer",
                section: "primary footer",
                text: buttonText,
              });
              handleModuleHome();
            }}>
            {courseMetadata?.language_overrides?.module?.singular ?? "Module"} home
          </ButtonZoom>
          <ButtonZoom
            sx={{ marginLeft: "24px" }}
            className={classes.barButton}
            size="large"
            variant="contained"
            color="primary"
            onClick={() => {
              const buttonText = `${
                currentModuleComplete
                  ? "Move forward"
                  : nextQuestion
                  ? `Next ${
                      targetLangOverrides?.short_singular?.toLowerCase() ??
                      targetLangOverrides?.singular?.toLowerCase() ??
                      "target"
                    }`
                  : "Exit"
              }`.toLowerCase();
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "footer",
                section: "primary footer",
                text: buttonText,
              });
              handleMoveForward();
            }}>
            {currentModuleComplete
              ? "Move forward"
              : nextQuestion
              ? `Next ${
                  targetLangOverrides?.short_singular?.toLowerCase() ??
                  targetLangOverrides?.singular?.toLowerCase() ??
                  "target"
                }`
              : "Exit"}
            <SvgIcon className={classes.arrow}>
              <ArrowIcon />
            </SvgIcon>
          </ButtonZoom>
        </>
      }>
      <Box className={classes.rootv2}>
        <Stack direction="column">
          {useAWSTargetEnd ? (
            <Stack direction="column" sx={{ position: "relative", marginBottom: "36px" }}>
              <Typography
                variant="h1"
                fontSize="40px"
                lineHeight="52px"
                sx={{ marginBottom: "24px" }}>
                Keep going, {currentUser.first_name}!
              </Typography>

              <Stack direction="column" sx={{ position: "relative" }}>
                {/* Empty Stack added to ensure proper screen reader hierarchy and prevent heading detection issues */}
                <Stack direction="row" alignItems="center"></Stack>
                <Typography
                  variant="h1"
                  sx={{
                    display: "block",
                    lineHeight: "24px",
                    fontWeight: 700,
                    fontSize: 24,
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ marginBottom: "16px" }}>
                    <IconCheck
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#48A84A",
                        marginRight: "8px",
                      }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      lineHeight="16px"
                      fontWeight={400}
                      fontSize={16}
                      sx={{ letterSpacing: "1.6px" }}
                      textTransform="uppercase">
                      {targetLangOverrides?.singular?.toUpperCase() ?? "Learning Target"}{" "}
                      Completed
                    </Typography>
                  </Stack>
                  {currentTargetName}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginTop: "24px" }}>
                <ModuleProgressIndicator
                  moduleId={currentModule.id}
                  percentage={currentModule.progress}
                  moduleName={currentModuleName}
                  style={classes.percentageStatusV2}
                  progressBarSx={{
                    position: "relative",
                    flexGrow: 1,
                    display: "inline-block",
                  }}
                />
              </Stack>
            </Stack>
          ) : (
            <Stack direction="column" sx={{ position: "relative", marginBottom: "36px" }}>
              {averagePercent >= 0.8 ? (
                <Box
                  sx={{ position: "absolute", right: "-60px", top: "-180px" }}
                  aria-hidden="true">
                  <Lottie
                    lottieRef={lottieRef}
                    className={classes.targetCompletedAnimationSmall}
                    animationData={TargetCompletedAnimation}
                    loop={false}
                    autoplay={false}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    right: "-20px",
                    top: "-160px",
                    zIndex: -1,
                  }}
                  aria-hidden="true">
                  <Box sx={{ width: "200px", height: "auto" }}>
                    {quizScore.invalid !== "No Quiz" && (
                      <Lottie
                        lottieRef={lottieRef}
                        className={classes.targetCompletedAnimationSmall}
                        animationData={TargetScoreLessThan80Animation}
                        loop={false}
                        autoplay={false}
                      />
                    )}
                  </Box>
                </Box>
              )}
              {/* Empty Stack added to ensure proper screen reader hierarchy and prevent heading detection issues */}
              <Stack direction="row" alignItems="center"></Stack>
              <Typography
                variant="h1"
                sx={{
                  display: "block",
                  lineHeight: "24px",
                  fontWeight: 700,
                  fontSize: 24,
                }}>
                <Stack direction="row" alignItems="center" sx={{ marginBottom: "16px" }}>
                  <IconCheck
                    style={{
                      width: "16px",
                      height: "16px",
                      color: "#48A84A",
                      marginRight: "8px",
                    }}
                    aria-hidden="true"
                  />
                  <Typography
                    variant="body2"
                    lineHeight="16px"
                    fontWeight={400}
                    fontSize={16}
                    sx={{ letterSpacing: "1.6px" }}
                    textTransform="uppercase">
                    {targetLangOverrides?.singular ?? "Learning Target"} Completed
                  </Typography>
                </Stack>
                {currentTargetName}
              </Typography>
            </Stack>
          )}
          <StatusCard
            status={
              quizScore.invalid === "No Quiz"
                ? {
                    text: "Keep going",
                    color: "success",
                  }
                : averagePercent >= 0.8
                ? {
                    text: "Excellent",
                    color: "success",
                  }
                : {
                    text: "Keep trying",
                    color: "warning",
                  }
            }
            buttons={
              <>
                <button
                  className={classes.reviewButton}
                  onClick={() => {
                    handleReview();
                    generateGtmEvent({
                      event: "link",
                      action: "click",
                      name: "onclick",
                      type: "internal link",
                      region: "main content",
                      section: averagePercent >= 0.8 ? "Excellent" : "Keep trying",
                      text: reviewButton.buttonText,
                      component: currentTargetName,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.currentTarget.click();
                    }
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing="8px">
                    <i className={reviewButton.iconClasses}></i>
                    <Typography
                      component="span"
                      variant="h4"
                      fontSize="inherit"
                      fontWeight={700}>
                      {reviewButton.buttonText}
                    </Typography>
                  </Stack>
                </button>
                {closeTabMessage && (
                  <Stack
                    direction="column"
                    gap="24px"
                    sx={{ marginTop: "24px", paddingX: "16px", paddingBottom: "16px" }}>
                    <Typography
                      variant="h4"
                      fontSize="24px"
                      lineHeight="26px"
                      fontWeight={600}>
                      Next lesson unlocks in Canvas
                    </Typography>
                    <Typography
                      variant="body1"
                      fontSize="14px"
                      lineHeight="24px"
                      letterSpacing="0.17px"
                      color="#484848">
                      To keep progressing, return to Canvas and open the next lesson once
                      it becomes available. Your work here is saved and recorded.
                    </Typography>
                    <Stack direction="row" gap="16px">
                      <ButtonZoom
                        className={classes.button}
                        size="medium"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          generateGtmEvent({
                            event: "link",
                            action: "click",
                            name: "onclick",
                            type: "internal link",
                            region: "main content",
                            section: "next lesson unlocks in canvas",
                            text: "module home",
                          });
                          handleModuleHome();
                        }}>
                        {courseMetadata?.language_overrides?.module?.singular ?? "Module"}{" "}
                        home
                      </ButtonZoom>
                      <ButtonZoom
                        className={classes.button}
                        size="medium"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          generateGtmEvent({
                            event: "link",
                            action: "click",
                            name: "onclick",
                            type: "internal link",
                            region: "main content",
                            section: "next lesson unlocks in canvas",
                            text: "back to canvas",
                          });
                          handleBackToCanvas();
                        }}>
                        Back to Canvas
                      </ButtonZoom>
                    </Stack>
                  </Stack>
                )}
              </>
            }>
            <Box sx={{ marginBottom: "24px" }}>
              {quizScore.display_as === "points" ? (
                <>
                  {quizScore.invalid === "No Quiz" ? (
                    <Typography
                      component="h2"
                      variant="h2"
                      lineHeight="26px"
                      fontWeight={600}
                      fontSize={24}>
                      Dive deeper
                    </Typography>
                  ) : (
                    <Box
                      component="h2"
                      sx={{ marginTop: "0px", display: "flex", flexDirection: "column" }}>
                      <Tooltip
                        placement="bottom-start"
                        arrow
                        title="This is your score for the graded quiz in this lesson. It reflects your accuracy and counts toward your overall grade.">
                        <Typography
                          component="span"
                          variant="body1"
                          lineHeight="24px"
                          fontWeight={500}
                          fontSize={16}
                          color="#005685"
                          sx={{
                            paddingX: "4px",
                            marginBottom: "16px",
                            backgroundColor: "#E0F4FF",
                            borderRadius: "4px",
                            marginRight: "auto",
                          }}>
                          <i className="fa-solid fa-star"></i>
                          <Box
                            sx={{ marginLeft: "8px" }}
                            component="span"
                            textTransform="uppercase">
                            Lesson Grade
                          </Box>
                        </Typography>
                      </Tooltip>
                      <Typography
                        component="span"
                        variant="h2"
                        lineHeight="26px"
                        fontWeight={700}
                        fontSize={40}
                        sx={{ marginBottom: "8px" }}>
                        {`${quizScore.points}/${quizScore.max_points}`}
                      </Typography>
                      <Typography
                        component="span"
                        variant="h2"
                        lineHeight="26px"
                        fontSize={16}
                        fontWeight={600}
                        sx={{ display: "block" }}>
                        Points
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {!metadataTotals.primary_quizzes ? (
                    <Typography
                      component="h2"
                      variant="h2"
                      lineHeight="26px"
                      fontWeight={600}
                      fontSize={24}>
                      Dive deeper
                    </Typography>
                  ) : (
                    <h2>
                      <Typography
                        component="span"
                        variant="h2"
                        lineHeight="26px"
                        fontWeight={700}
                        fontSize={40}
                        sx={{ marginBottom: "8px" }}>
                        {Math.round(averagePercent * 100)}%
                      </Typography>
                      <Typography
                        component="span"
                        variant="body1"
                        lineHeight="26px"
                        fontSize={16}
                        fontWeight={600}
                        sx={{ display: "block" }}>
                        Average score
                      </Typography>
                    </h2>
                  )}
                </>
              )}
            </Box>
            {courseMetadata?.lti_grading_method === "completion" ||
            !courseMetadata?.lti_grading_method ? (
              <>
                {!hasSupplementalLearning ? (
                  <Typography variant="body2" fontSize="14px" lineHeight="24px">
                    You can review and retry as many times as needed without lowering your
                    score.
                  </Typography>
                ) : (
                  <Typography variant="body2" fontSize="14px" lineHeight="24px">
                    You can review and retry as many times as needed without lowering your
                    score. Check to make sure you did all the "
                    <Link
                      href="#additional-learning-container"
                      aria-label="You can review and retry as many times as needed without lowering your score. Check to make sure you did all the additional learning"
                      className={classes.link}
                      onClick={(event) => {
                        event.preventDefault();
                        generateGtmEvent({
                          event: "modal",
                          action: "open",
                          name: "onclick",
                          type: "click",
                          region: "main content",
                          section: averagePercent >= 0.8 ? "Excellent" : "Keep trying",
                          text: "additional learning",
                          component: currentTargetName,
                        });
                        setTutorialToastOpen(true);
                        setTimeout(() => {
                          document
                            .getElementById("additional-learning-container")
                            ?.focus();
                        }, 100); // Small delay ensures popover is rendered first
                      }}>
                      additional learning
                    </Link>
                    ".
                  </Typography>
                )}
              </>
            ) : (
              <>
                {quizScore.display_as === "points" && quizScore.invalid !== "No Quiz" ? (
                  <Typography variant="body2" fontSize="14px" lineHeight="24px">
                    You can review and retry as many times as needed without lowering your
                    score. Check to make sure you did all the "
                    <Link
                      href="#additional-learning-container"
                      aria-label="You can review and retry as many times as needed without lowering your score. Check to make sure you did all the additional learning"
                      className={classes.link}
                      onClick={(event) => {
                        event.preventDefault();
                        generateGtmEvent({
                          event: "modal",
                          action: "open",
                          name: "onclick",
                          type: "click",
                          region: "main content",
                          section: averagePercent >= 0.8 ? "Excellent" : "Keep trying",
                          text: "additional learning",
                          component: currentTargetName,
                        });
                        setTutorialToastOpen(true);
                        setTimeout(() => {
                          document
                            .getElementById("additional-learning-container")
                            ?.focus();
                        }, 100); // Small delay ensures popover is rendered first
                      }}>
                      additional learning
                    </Link>
                    ".
                  </Typography>
                ) : (
                  <Typography variant="body2" fontSize="14px" lineHeight="24px">
                    You can review this lesson as many times as you'd like. Make sure to
                    check out all the "
                    <Link
                      href="#additional-learning-container"
                      aria-label="You can review this lesson as many times as you'd like. Make sure to check out all the additional learning"
                      className={classes.link}
                      onClick={(event) => {
                        event.preventDefault();
                        generateGtmEvent({
                          event: "modal",
                          action: "open",
                          name: "onclick",
                          type: "click",
                          region: "main content",
                          section: "dive deeper",
                          text: "additional learning",
                        });
                        setTutorialToastOpen(true);
                        setTimeout(() => {
                          document
                            .getElementById("additional-learning-container")
                            ?.focus();
                        }, 100); // Small delay ensures popover is rendered first
                      }}>
                      additional learning
                    </Link>
                    " for a deeper understanding.
                  </Typography>
                )}
              </>
            )}
          </StatusCard>
          {showRateDigit ? (
            <Box sx={{ marginTop: "16px" }}>
              <RateDigit
                key={`rate-digit-${currentUser.id}-${currentTarget.id}`}
                currentUser={currentUser}
                assetId={currentTarget.id}
                assetType={"Module"}
                currentClass={currentClass}
                currentCourse={currentCourse}
                courseMetadata={courseMetadata}
              />
            </Box>
          ) : (
            <Box sx={{ marginTop: "36px" }}>
              <StatusCard
                status={{
                  text: "Good job",
                  color: "success",
                }}>
                <Typography
                  variant="h2"
                  lineHeight="26px"
                  fontWeight={600}
                  fontSize={24}
                  marginBottom="8px">
                  {courseMetadata?.deeplink_asset_type === "target"
                    ? "Lesson"
                    : courseMetadata?.language_overrides?.module?.singular ??
                      "Module"}{" "}
                  progress
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ marginBottom: "12px" }}
                  fontSize="14px"
                  lineHeight="24px">
                  Keep going! You’ve made great progress—finish strong!
                </Typography>
                <Stack direction="row" alignItems="center">
                  <ModuleProgressIndicator
                    moduleId={currentModule.id}
                    percentage={currentModule.progress}
                    moduleName={currentModuleName}
                    style={classes.percentageStatus}
                    progressBarSx={{
                      position: "relative",
                      width: "214px",
                      display: "inline-block",
                    }}
                  />
                </Stack>
              </StatusCard>
            </Box>
          )}
        </Stack>
      </Box>
    </WrapperCenterMessage>
  );
};
